import React, { useContext, memo, useMemo } from 'react';
import each from 'lodash/each';
import get from 'lodash/get';
import isNil from 'lodash/isNil';

import IvrTreeContext from 'src/presentation/components/pages/ivr/components/ivrTree/ivrTreeContext';
import StartNode from './startNode';
import TreeData from 'src/presentation/components/pages/ivr/components/ivrTree/models/TreeData';
import { IvrLink } from './ivrLink';
import { IvrNodeBlock } from './ivrTreeNode';
import { IvrPathWithLink, IvrNode } from 'src/presentation/components/pages/ivr/components/ivrTree/ivrTypes';
import { TextNode } from './optionNode';
import { TreeLayoutNodeTypeEnum } from 'src/presentation/components/pages/ivr/constants/ivrTreeConstants';

const RootTree = ({ className, treeData }: { className: string; treeData: TreeData }) => {
  const { selectedNodeId } = useContext(IvrTreeContext);

  const hightLightedNodesIds = useMemo(
    () => (selectedNodeId ? [...Array.from(treeData.getAllAncestors(selectedNodeId).keys()), selectedNodeId] : []),
    [treeData, selectedNodeId]
  );

  return (
    <g className={className}>
      <Nodes nodes={treeData.tree} highLightedIds={hightLightedNodesIds} selectedNodeId={selectedNodeId} />
      <MemoizedLinks treeData={treeData} highLightedIds={hightLightedNodesIds} />
    </g>
  );
};

const Links = ({ treeData, highLightedIds }) => {
  const highlighted: IvrPathWithLink[] = [];
  const notHighlighted: IvrPathWithLink[] = [];

  each(treeData.links, (link: IvrPathWithLink) => {
    let isHighlighted = highLightedIds.includes(link.target.id) && link.source.ref !== link.target.id;
    if (highLightedIds.length === 1) {
      if (link.option) {
        const textId = `${link.source.id}-${
          isNil(link.optionIndex) ? link.option.replace(' ', '-') : link.optionIndex
        }`;
        isHighlighted = isHighlighted || highLightedIds.includes(textId);
      }
    }
    if (isHighlighted) {
      highlighted.push(link);
    } else {
      notHighlighted.push(link);
    }
  });

  const { ivrFrameSize } = useContext(IvrTreeContext);
  const marginLeft = get(ivrFrameSize, 'marginLeft', 0);
  const marginTop = get(ivrFrameSize, 'marginTop', 0);

  return [
    ...notHighlighted.map((link: IvrPathWithLink) => {
      const key = `${link.source.id}-${link.target.id}${link.option ? `-${link.option}-${link.optionIndex}` : ''}`;
      return <IvrLink link={link} key={key} marginLeft={marginLeft} marginTop={marginTop} />;
    }),
    ...highlighted.map((link: IvrPathWithLink) => {
      const key = `${link.source.id}-${link.target.id}${link.option ? `-${link.option}-${link.optionIndex}` : ''}`;
      const isSelected =
        highLightedIds.length === 1 &&
        highLightedIds[0] ===
          (isNil(link.optionIndex)
            ? `${link.source.id}-${(link.option || '').replace(' ', '-')}`
            : `${link.source.id}-${link.optionIndex}`);

      return (
        <IvrLink
          link={link}
          selected={isSelected}
          highlighted
          key={key}
          marginLeft={marginLeft}
          marginTop={marginTop}
        />
      );
    }),
  ];
};

const MemoizedLinks = memo(Links as any);

const Nodes: any = ({
  nodes,
  highLightedIds,
  selectedNodeId,
}: {
  nodes: any;
  highLightedIds: string[];
  selectedNodeId: string;
}) => {
  const { ivrFrameSize } = useContext(IvrTreeContext);

  if (!nodes) return null;
  const marginLeft = get(ivrFrameSize, 'marginLeft', 0);
  const marginTop = get(ivrFrameSize, 'marginTop', 0);
  return nodes
    .filter((node: IvrNode) => node.id)
    .map((node: any) => {
      const id = node.id;

      const Node =
        node.nodeType === TreeLayoutNodeTypeEnum.OPTION
          ? TextNode
          : node.nodeType === TreeLayoutNodeTypeEnum.NODE
          ? IvrNodeBlock
          : StartNode;

      const props = {
        x: node.x,
        y: node.y,
        width: node.width,
        height: node.height,
        text: get(node, 'name', ''),
        highlighted: highLightedIds.includes(node.id),
        selected: node.id === selectedNodeId,
        node: node,
        transform: `translate(${marginLeft},${marginTop})`,
        data: node,
      };
      return <Node data-aid={`ivrTreeNode-${node.id}`} id={node.id} key={id} {...props} />;
    });
};

export { RootTree };
